"use client"

import { cn } from "@/utils/helpers"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { useTranslation } from "react-i18next"
import { Button } from "../Button"
import { DialogCommonClose } from "./DialogCommon"
import { InfoBox, InfoBoxProps } from "../InfoBoxes"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Close>
>(({ children, ...props }, ref) => {
  const { t } = useTranslation("common")
  const hasChildren = React.Children.count(children) > 0

  const Comp = hasChildren ? (
    children
  ) : (
    <Button
      text={t("close")}
      variant="text"
      size="icon"
      iconName="close"
      type="button"
      className="text-on-surface-variant m-1 ml-auto"
    />
  )

  return (
    <SheetPrimitive.Close
      {...props}
      asChild={!hasChildren || props.asChild}
      ref={ref}
    >
      {Comp}
    </SheetPrimitive.Close>
  )
})
SheetClose.displayName = SheetPrimitive.Close.displayName

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-scrim fixed inset-0 z-50 !duration-300",
      className,
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  [
    "transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
    "fixed z-50 bg-container-lowest overflow-clip",
  ],
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b rounded-b-2xl data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t rounded-t-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full rounded-r-2xl data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left w-[34rem] flex flex-col max-w-[calc(100vw-2rem)]",
        right:
          "inset-y-0 right-0 h-full rounded-l-2xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right w-[34rem] flex flex-col max-w-[calc(100vw-2rem)]",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  overlay?: boolean
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, overlay = true, ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement>(null)
  React.useImperativeHandle(ref, () => contentRef.current)

  return (
    <SheetPortal>
      <SheetOverlay className={cn(!overlay && "opacity-0")} />
      <SheetPrimitive.Content
        ref={contentRef}
        className={cn(sheetVariants({ side }), className)}
        {...props}
        // Our Sheet designs never include a description, so this silences the warnings. */
        aria-describedby={props["aria-describedby"] ?? undefined}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
          props.onCloseAutoFocus?.(event)
        }}
        onPointerDownOutside={(e) => {
          /**
           * Prevents the sonner-toast from closing the Sheet
           * Copied from here: https://github.com/radix-ui/primitives/issues/2690#issuecomment-1945449832
           */
          if (e.target instanceof Element) {
            if (e.target.closest("[data-sonner-toast]")) {
              e.preventDefault()
            }

            if (
              e.target.closest("[data-radix-dropdown-menu-content]") ||
              e.target.closest("[data-radix-dropdown-menu-sub-content]")
            ) {
              e.preventDefault()
            }
          }
          props.onPointerDownOutside?.(e)
        }}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetInnerContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      {...props}
      ref={ref}
      className={cn(
        "flex flex-1 flex-col gap-6 overflow-y-auto py-6 pl-6 pr-[calc(1.5rem-var(--scrollbar-width))]",
        className,
      )}
      style={{
        scrollbarGutter: "stable",
      }}
    >
      {children}
    </div>
  )
})
SheetInnerContent.displayName = "SheetInnerContent"

/**
 * Usually only contains a single string/text-element child.
 * Styling will adapt if there are two (or more) elements, in case the design needs a button to the left of the title.
 * Note: Only the last child is used as the accessibility title.
 */
const SheetHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const childrenArray = React.Children.toArray(children)
  const childrenCount = childrenArray.length
  const lastChild = childrenArray.pop()

  return (
    <>
      <VisuallyHidden>{lastChild}</VisuallyHidden>
      <div
        className={cn(
          "border-outline-variant flex flex-row items-center justify-start border-b py-4 pb-4 pl-6 pr-3 pt-3 text-left",
          childrenCount > 1 && "text gap-1 pl-2",
          className,
        )}
        {...props}
      >
        {childrenArray}
        <SheetTitle>{lastChild}</SheetTitle>
        <DialogCommonClose className="ml-auto" />
      </div>
    </>
  )
}
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  infoBoxProps,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { infoBoxProps?: InfoBoxProps }) => (
  <div className="border-outline-variant border-t">
    {infoBoxProps && (
      <InfoBox {...infoBoxProps} className="rounded-none px-6" />
    )}
    <div className={cn("flex flex-row gap-2 p-6 pt-4", className)} {...props} />
  </div>
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-heading-2xs text-on-surface-variant h-fit", className)}
    {...props}
    // Makes the title focusable. This prevents the close button from being autofocused.
    tabIndex={0}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  SheetInnerContent,
}
